import React from "react";
import { useNavigate } from "react-router-dom";
import { FaLock, FaSearch, FaServer, FaArrowLeft, FaShieldAlt } from "react-icons/fa";

/* ─────────────────────────────────────────────────────────
   Shared style injection (keyframes + hover states can't be
   expressed with inline styles, so we inject once globally)
───────────────────────────────────────────────────────── */
const GlobalStatusStyles = () => (
  <style>{`
    @keyframes statusFadeUp {
      from { opacity: 0; transform: translateY(10px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes statusGlowPulse {
      0%, 100% { opacity: 0.45; transform: scale(1); }
      50%      { opacity: 0.65; transform: scale(1.03); }
    }

    .status-page-root {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: 100vh;
      min-height: 100dvh;
      padding: 40px 20px;
      box-sizing: border-box;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: radial-gradient(circle at 50% 0%, var(--primary-glow), transparent 60%), var(--bg-main);
    }

    .status-page-card {
      position: relative;
      background: var(--bg-card);
      border: 1px solid var(--border-color);
      border-radius: var(--radius);
      padding: 48px 40px 40px;
      max-width: 440px;
      width: 100%;
      text-align: center;
      box-shadow: var(--shadow-lg);
      animation: statusFadeUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) both;
      overflow: hidden;
    }

    // .status-page-card::before {
    //   content: "";
    //   position: absolute;
    //   top: 0; left: 0; right: 0;
    //   height: 4px;
    //   background: var(--accent-gradient, var(--primary-gradient));
    // }

    .status-icon-glow {
      position: absolute;
      top: -20px;
      left: 50%;
      transform: translateX(-50%);
      width: 110px;
      height: 110px;
      border-radius: 50%;
      background: var(--accent-glow, var(--primary-glow));
      filter: blur(24px);
      animation: statusGlowPulse 4s ease-in-out infinite;
      pointer-events: none;
    }

    .status-icon-wrap {
      position: relative;
      width: 68px;
      height: 68px;
      margin: 0 auto 22px;
      border-radius: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 26px;
      background: var(--accent-gradient, var(--primary-gradient));
      color: #ffffff;
      box-shadow: var(--shadow-md);
    }

    .status-code {
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--accent-color, var(--primary));
      margin: 0 0 8px;
    }

    .status-title {
      font-size: 1.5rem;
      font-weight: 700;
      letter-spacing: -0.01em;
      color: var(--text-main);
      margin: 0 0 14px;
    }

    .status-desc {
      color: var(--text-muted);
      font-size: 0.95rem;
      line-height: 1.65;
      margin: 0 0 30px;
    }

    .status-perm-chip {
      display: inline-block;
      margin-top: 12px;
      padding: 6px 14px;
      border-radius: 999px;
      font-size: 0.8rem;
      font-weight: 600;
      font-family: 'JetBrains Mono', Menlo, Monaco, Consolas, monospace;
      background: color-mix(in srgb, var(--danger) 12%, transparent);
      color: var(--danger);
      border: 1px solid color-mix(in srgb, var(--danger) 30%, transparent);
    }

    .status-btn-row {
      display: flex;
      gap: 12px;
      justify-content: center;
      flex-wrap: wrap;
    }

    .status-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 11px 24px;
      border-radius: 10px;
      font-weight: 600;
      font-size: 0.88rem;
      cursor: pointer;
      transition: var(--transition);
      border: 1px solid transparent;
      font-family: inherit;
    }

    .status-btn-primary {
      background: var(--accent-gradient, var(--primary-gradient));
      color: #ffffff;
      box-shadow: var(--shadow-md);
    }
    .status-btn-primary:hover {
      transform: translateY(-2px);
      filter: brightness(1.06);
      box-shadow: var(--shadow-lg);
    }

    .status-btn-secondary {
      background: var(--bg-main);
      color: var(--text-main);
      border: 1px solid var(--border-color);
    }
    .status-btn-secondary:hover {
      transform: translateY(-2px);
      border-color: var(--accent-color, var(--primary));
      color: var(--accent-color, var(--primary));
    }
  `}</style>
);

const Shell = ({ accent, accentGradient, glow, code, icon, title, children }) => (
  <div className="status-page-root">
    <GlobalStatusStyles />
    <div
      className="status-page-card"
      style={{
        "--accent-color": accent,
        "--accent-gradient": accentGradient,
        "--accent-glow": glow,
      }}
    >
      <div className="status-icon-glow" />
      <div className="status-icon-wrap">{icon}</div>
      <p className="status-code">{code}</p>
      <h1 className="status-title">{title}</h1>
      {children}
    </div>
  </div>
);

/* ───────────────────────── 401 ───────────────────────── */
export const Unauthorized401 = () => {
  const navigate = useNavigate();
  return (
    <Shell
      accent="var(--warning)"
      accentGradient="linear-gradient(135deg, #d97706, #f59e0b)"
      glow="rgba(217, 119, 6, 0.25)"
      code="Error 401"
      icon={<FaLock />}
      title="Authentication Required"
    >
      <p className="status-desc">
        Your session has expired or you're not logged in. Sign in with your
        credentials to access this secure HRMS resource.
      </p>
      <div className="status-btn-row">
        <button className="status-btn status-btn-primary" onClick={() => navigate("/login")}>
          <FaLock style={{ fontSize: "0.8rem" }} /> Login Now
        </button>
      </div>
    </Shell>
  );
};

/* ───────────────────────── 403 ───────────────────────── */
export const AccessDenied403 = ({ requiredPerm }) => {
  const navigate = useNavigate();
  return (
    <Shell
      accent="var(--danger)"
      accentGradient="linear-gradient(135deg, #dc2626, #ef4444)"
      glow="rgba(220, 38, 38, 0.25)"
      code="Error 403"
      icon={<FaShieldAlt />}
      title="Access Denied"
    >
      <p className="status-desc">
        You don't have permission to view or interact with this resource.
        {requiredPerm && (
          <span className="status-perm-chip">Required: {requiredPerm}</span>
        )}
        <br />
        <br />
        If you need access for your daily job responsibilities, please
        contact your HR Manager or IT Administrator.
      </p>
      <div className="status-btn-row">
        <button className="status-btn status-btn-secondary" onClick={() => navigate(-1)}>
          <FaArrowLeft style={{ fontSize: "0.8rem" }} /> Go Back
        </button>
        {/* <button className="status-btn status-btn-primary" onClick={() => navigate("/dashboard")}>
          <FaHome style={{ fontSize: "0.8rem" }} /> Dashboard
        </button> */}
      </div>
    </Shell>
  );
};

/* ───────────────────────── 404 ───────────────────────── */
export const NotFound404 = () => {
  const navigate = useNavigate();
  return (
    <Shell
      accent="var(--primary)"
      accentGradient="var(--primary-gradient)"
      glow="var(--primary-glow)"
      code="Error 404"
      icon={<FaSearch />}
      title="Page Not Found"
    >
      <p className="status-desc">
        The page or resource you're looking for doesn't exist, has been
        moved, or is temporarily unavailable. Check the URL or head back to
        safety.
      </p>
      <div className="status-btn-row">
        <button className="status-btn status-btn-secondary" onClick={() => navigate(-1)}>
          <FaArrowLeft style={{ fontSize: "0.8rem" }} /> Go Back
        </button>
        {/* <button className="status-btn status-btn-primary" onClick={() => navigate("/dashboard")}>
          <FaHome style={{ fontSize: "0.8rem" }} /> Dashboard
        </button> */}
      </div>
    </Shell>
  );
};

/* ───────────────────────── 500 ───────────────────────── */
export const ServerError500 = () => {
  return (
    <Shell
      accent="var(--danger)"
      accentGradient="linear-gradient(135deg, #dc2626, #f43f5e)"
      glow="rgba(220, 38, 38, 0.25)"
      code="Error 500"
      icon={<FaServer />}
      title="Server Error"
    >
      <p className="status-desc">
        Our server encountered an unexpected error while processing your
        request. Our technical team has been notified — please try again
        shortly.
      </p>
      <div className="status-btn-row">
        <button className="status-btn status-btn-secondary" onClick={() => window.location.reload()}>
          Refresh Page
        </button>
        {/* <button className="status-btn status-btn-primary" onClick={() => navigate("/dashboard")}>
          <FaHome style={{ fontSize: "0.8rem" }} /> Dashboard
        </button> */}
      </div>
    </Shell>
  );
};